import jsPDF from 'jspdf';
import { render as renderRecibo } from '@/lib/pdfTemplates/recibo';
import { hexToRgb, type PDFQuoteData } from '@/lib/pdfTemplates/shared';
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import { receiptPdfItems, receiptPaymentLabel } from '@/lib/receiptItems';
import type { PaymentMethod } from '@/types/database';

/**
 * Exportação de VÁRIOS recibos num PDF só — um recibo por página.
 *
 * Um PDF por recibo obrigaria o usuário a repetir o fluxo dezenas de vezes e,
 * no Android, a atravessar o "Abrir com" em cada um. O caso real é fechamento
 * de mês: selecionar os recibos do período e arquivar/enviar um arquivo.
 *
 * ─── AS DUAS ARMADILHAS DESTE ARQUIVO ────────────────────────────────────────
 *
 * 1. **O rodapé varre TODAS as páginas.** `renderFooter` escreve o número do
 *    documento (`#0042`) e percorre `1..getNumberOfPages()`. Renderizar o 2º
 *    recibo redesenharia o rodapé sobre a página do 1º, sobrepondo dois números
 *    no mesmo ponto. Por isso cada recibo informa em que página ele começa
 *    (`fromPage`) — e é preciso capturar isso ANTES de renderizar, porque um
 *    recibo com muito texto pode ocupar mais de uma página.
 *
 * 2. **A primeira página já existe.** `new jsPDF()` nasce com uma página. Um
 *    `addPage()` antes do primeiro recibo produziria uma folha em branco na
 *    frente do arquivo — o tipo de defeito que só aparece imprimindo.
 */

export interface ReceiptRowForExport {
  id?: string;
  payer_name?: string | null;
  payer_document?: string | null;
  payer_address?: string | null;
  amount?: number | string | null;
  description?: string | null;
  payment_method?: string | null;
  /** Formas USADAS (pagamento dividido) e condições combinadas. */
  payment_methods?: string[] | null;
  payment_terms?: string | null;
  /** Linhas discriminadas do recibo (embed `receipt_items(*)`). */
  receipt_items?: Array<Record<string, unknown>> | null;
  issue_date?: string | null;
  issue_place?: string | null;
  issuer_signature?: string | null;
  issuer_signer_name?: string | null;
  notes?: string | null;
  sequence_id?: number | null;
  show_sequence_id?: boolean | null;
  cancelled_at?: string | null;
}

export interface ReceiptExportCompany {
  name?: string | null;
  cnpj_cpf?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  logo_watermark?: boolean | null;
}

export interface ReceiptExportOptions {
  company?: ReceiptExportCompany | null;
  /** `no_branding_footer` — mesma regra de todos os documentos. */
  isPremium: boolean;
}

/** Converte uma linha de `receipts` no shape que o template de recibo espera. */
export function receiptRowToPdfData(
  row: ReceiptRowForExport,
  opts: ReceiptExportOptions,
): PDFQuoteData {
  const company = opts.company;
  const paymentLabel = receiptPaymentLabel(row, PAYMENT_METHOD_LABELS);

  return {
    documentType: 'recibo',
    client: {
      name: row.payer_name || 'Não informado',
      address: row.payer_address || undefined,
      document: row.payer_document || undefined,
    },
    company: company
      ? {
          name: company.name || 'Minha Empresa',
          cnpj: company.cnpj_cpf || undefined,
          address: company.address || undefined,
          phone: company.phone || undefined,
          email: company.email || undefined,
          logoUrl: company.logo_url || undefined,
        }
      : undefined,
    // `receipts.issue_date` é DATE: a meia-noite LOCAL evita o drift de um dia
    // que `new Date('YYYY-MM-DD')` (parseado como UTC) produz em UTC-3.
    quoteDate: row.issue_date
      ? `${String(row.issue_date).split('T')[0]}T00:00:00`
      : new Date().toISOString(),
    // Mesma conversao da tela de detalhe: montada a mao nos dois, ela
    // divergiria e o mesmo recibo sairia diferente no lote e no individual.
    items: receiptPdfItems(row),
    total: Number(row.amount ?? 0),
    isPremium: opts.isPremium,
    brandColorRGB: company?.primary_color ? hexToRgb(company.primary_color) : [13, 148, 136],
    sequenceId: row.sequence_id ?? undefined,
    showSequenceId: row.show_sequence_id ?? false,
    receiptDescription: row.description,
    receiptPaymentMethodLabel: paymentLabel,
    receiptIssuePlace: row.issue_place,
    receiptNotes: row.notes,
    receiptPaymentTerms: row.payment_terms,
    issuerSignatureDataUrl: row.issuer_signature,
    issuerSignerName: row.issuer_signer_name,
    logoWatermark: company?.logo_watermark === true,
  };
}

/**
 * Monta o PDF com todos os recibos informados, um por página, na ordem em que
 * vieram. Devolve o documento — quem chama decide como entregá-lo (ver
 * `savePdfDocument`, que trata a separação nativo/web).
 */
export async function buildReceiptsPdf(
  rows: ReceiptRowForExport[],
  opts: ReceiptExportOptions,
): Promise<jsPDF> {
  const doc = new jsPDF();

  for (let i = 0; i < rows.length; i++) {
    // A página 1 já existe; só a partir do 2º recibo é que se cria página.
    if (i > 0) doc.addPage();
    // Capturado ANTES de renderizar: o recibo pode crescer para mais de uma
    // página, e o rodapé precisa saber onde ESTE recibo começa.
    const fromPage = doc.getNumberOfPages();
    await renderRecibo(doc, receiptRowToPdfData(rows[i], opts), { fromPage });
  }

  return doc;
}

/** Nome do arquivo da exportação, com a contagem visível. */
export function receiptsExportFilename(count: number, todayIso: string): string {
  return `recibos-${count}-${todayIso}.pdf`;
}
